<template>
  <Adminlte>
    <div class="box box-primary center-block">
      <div class="box-header with-border">
        <i class="fa fa-wrench font-dark"></i>
        <h3 class="box-title">Manutenção do Sistema</h3>
      </div>

      <section class="content">
        <div class="maintenance-section">
          <h4>Consistência de estágios dos parques</h4>
          <p class="help-text">
            Compara o estágio cadastrado do parque com o estágio calculado a partir das
            datas das Informações Gerais. A sincronização atualiza o cadastro com base nessas datas.
          </p>

          <div class="btn-row">
            <button
              class="btn btn-primary"
              @click="runAudit"
              :disabled="parkStagesLoading"
            >
              Auditar (somente leitura) &nbsp;
              <i class="fa fa-refresh fa-spin" v-if="parkStagesLoading && parkStagesAction === 'audit'"></i>
            </button>

            <button
              class="btn btn-warning"
              @click="runSync"
              :disabled="parkStagesLoading"
            >
              Sincronizar estágios &nbsp;
              <i class="fa fa-refresh fa-spin" v-if="parkStagesLoading && parkStagesAction === 'sync'"></i>
            </button>
          </div>

          <div v-if="parkStagesError" class="alert alert-danger">{{ parkStagesError }}</div>

          <div v-if="parkStagesResult" class="result-box">
            <p>
              <strong>{{ parkStagesResult.fixApplied ? 'Sincronização executada.' : 'Auditoria executada.' }}</strong>
              Total de inconsistências: <strong>{{ parkStagesResult.total }}</strong>
              <span v-if="parkStagesResult.fixApplied"> — Corrigidas: <strong>{{ parkStagesResult.fixed }}</strong></span>
            </p>

            <table v-if="parkStagesResult.inconsistencies.length" class="table table-striped table-bordered">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Parque</th>
                  <th>Problemas</th>
                  <th v-if="parkStagesResult.fixApplied">Corrigido</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in parkStagesResult.inconsistencies" :key="item.id">
                  <td>{{ item.id }}</td>
                  <td>{{ item.name }}</td>
                  <td>
                    <ul class="issues-list">
                      <li v-for="(issue, index) in item.issues" :key="index">{{ issue }}</li>
                    </ul>
                  </td>
                  <td v-if="parkStagesResult.fixApplied">
                    <span :class="item.fixed ? 'label label-success' : 'label label-default'">
                      {{ item.fixed ? 'Sim' : 'Não' }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>

            <p v-else class="text-success">Nenhuma inconsistência encontrada.</p>
          </div>
        </div>

        <hr>

        <div class="maintenance-section">
          <h4>Natureza jurídica da entidade gestora</h4>
          <p class="help-text">
            Atualiza as opções dos campos de natureza jurídica (administrativa e financeira)
            no banco de dados, incluindo a opção <strong>Empresa Pública</strong>.
            Equivalente ao comando <code>inovadata:update-management-entity-legal-nature-options</code>.
          </p>

          <div class="btn-row">
            <button
              class="btn btn-warning"
              @click="runLegalNatureUpdate"
              :disabled="legalNatureLoading"
            >
              Atualizar opções no banco &nbsp;
              <i class="fa fa-refresh fa-spin" v-if="legalNatureLoading"></i>
            </button>
          </div>

          <div v-if="legalNatureError" class="alert alert-danger">{{ legalNatureError }}</div>

          <div v-if="legalNatureResult" class="alert alert-success">
            {{ legalNatureResult.message }}
          </div>
        </div>

        <hr>

        <div class="maintenance-section">
          <h4>Áreas de terreno e edificações (por parque)</h4>
          <p class="help-text">
            Recria as respostas de áreas de terreno e edificações no questionário do parque
            a partir das Informações Gerais, quando estiverem inconsistentes.
            Equivalente ao comando <code>inovadata:fix-park-land-areas {parkId}</code>.
          </p>

          <div class="form-inline btn-row">
            <div class="form-group">
              <label for="parkIdInput">ID do parque</label>
              <input
                id="parkIdInput"
                v-model.number="parkId"
                type="number"
                min="1"
                class="form-control park-id-input"
                placeholder="Ex.: 73"
              >
            </div>
            <button
              class="btn btn-warning"
              @click="runLandAreasFix"
              :disabled="landAreasLoading || !parkId"
            >
              Corrigir áreas do parque &nbsp;
              <i class="fa fa-refresh fa-spin" v-if="landAreasLoading"></i>
            </button>
          </div>

          <div v-if="landAreasError" class="alert alert-danger">{{ landAreasError }}</div>

          <div
            v-if="landAreasResult"
            :class="landAreasResult.status === 'ok' ? 'alert alert-success' : 'alert alert-warning'"
          >
            {{ landAreasResult.message }}
          </div>
        </div>
      </section>
    </div>
  </Adminlte>
</template>

<script>
  import Adminlte from '@/components/template/Adminlte'
  import MaintenanceClient from '@/services/resource_client/maintenance'

  export default {
    name: 'ParkStageAudit',
    components: { Adminlte },
    data () {
      return {
        parkStagesLoading: false,
        parkStagesAction: null,
        parkStagesResult: null,
        parkStagesError: null,
        legalNatureLoading: false,
        legalNatureResult: null,
        legalNatureError: null,
        parkId: null,
        landAreasLoading: false,
        landAreasResult: null,
        landAreasError: null
      }
    },
    methods: {
      parseMaintenanceError (error) {
        if (!error || !error.response) {
          return 'Erro ao conectar com a API. Verifique sua conexão e tente novamente.'
        }

        const status = error.response.status
        const apiMessage = error.response.data && error.response.data.message

        if (status === 401) {
          return 'Sessão expirada ou token inválido. Faça login novamente como administrador.'
        }
        if (status === 403) {
          return 'Seu usuário não tem permissão para executar rotinas de manutenção.'
        }
        if (status === 404) {
          return 'Endpoint de manutenção não encontrado. Verifique se a API foi atualizada e se o cache de produção (var/cache/prod) foi limpo após o deploy.'
        }
        if (apiMessage) {
          return apiMessage
        }

        return 'Não foi possível executar a rotina. Tente novamente mais tarde.'
      },
      runAudit () {
        this.executeParkStages('audit', () => MaintenanceClient.auditParkStages())
      },
      runSync () {
        if (!window.confirm('Isso irá atualizar o estágio cadastrado dos parques com base nas datas das Informações Gerais. Deseja continuar?')) {
          return
        }
        this.executeParkStages('sync', () => MaintenanceClient.syncParkStages())
      },
      runLegalNatureUpdate () {
        if (!window.confirm('Isso irá atualizar as opções de natureza jurídica no banco de dados. Deseja continuar?')) {
          return
        }

        this.legalNatureLoading = true
        this.legalNatureError = null
        this.legalNatureResult = null

        MaintenanceClient.updateManagementEntityLegalNatureOptions()
          .then(response => {
            this.legalNatureResult = response.data
          })
          .catch((error) => {
            this.legalNatureError = this.parseMaintenanceError(error)
          })
          .finally(() => {
            this.legalNatureLoading = false
          })
      },
      runLandAreasFix () {
        if (!this.parkId) {
          return
        }

        if (!window.confirm('Isso irá recriar as respostas de áreas do questionário com base nas Informações Gerais. Deseja continuar?')) {
          return
        }

        this.landAreasLoading = true
        this.landAreasError = null
        this.landAreasResult = null

        MaintenanceClient.fixParkLandAreas(this.parkId)
          .then(response => {
            this.landAreasResult = response.data
          })
          .catch((error) => {
            this.landAreasError = this.parseMaintenanceError(error)
          })
          .finally(() => {
            this.landAreasLoading = false
          })
      },
      executeParkStages (action, request) {
        this.parkStagesLoading = true
        this.parkStagesAction = action
        this.parkStagesError = null
        request()
          .then(response => {
            this.parkStagesResult = response.data
          })
          .catch((error) => {
            this.parkStagesError = this.parseMaintenanceError(error)
          })
          .finally(() => {
            this.parkStagesLoading = false
          })
      }
    }
  }
</script>

<style scoped>
  .maintenance-section {
    margin-bottom: 20px;
  }
  .maintenance-section h4 {
    color: #333;
    margin-top: 0;
  }
  .help-text {
    color: #555;
    margin-bottom: 15px;
  }
  .btn-row {
    margin-bottom: 15px;
  }
  .btn-row .btn {
    margin-right: 10px;
  }
  .result-box {
    margin-top: 15px;
  }
  .issues-list {
    margin: 0;
    padding-left: 18px;
  }
  .park-id-input {
    width: 120px;
    margin-left: 8px;
    margin-right: 12px;
  }
  .form-inline .form-group label {
    color: #555;
    font-weight: normal;
  }
</style>



// WEBPACK FOOTER //
// ParkStageAudit.vue?9534389e